import {
  DashboardOrderDetailsResponse,
  DashboardOrderHistoriesResponse,
  useLazyDashboardOrderHistoriesQuery,
  useReEvaluateAllStepsMutation,
} from "@/shared/api";
import { QuestionMarkCircledIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  Flex,
  Badge,
  AlertDialog,
  Popover,
  ChevronDownIcon,
  Heading,
  DataList,
  Button,
  Tooltip,
  Text,
  ScrollArea,
} from "@radix-ui/themes";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

type ReEvaluationProps = {
  orderRequestHistory?: DashboardOrderDetailsResponse["result"]["order_request_history"];
  requestId: string | null;
  orderId: string;
  onSelectHistory: (requestId: string | undefined) => void;
};

export function ReEvaluation({
  orderRequestHistory,
  requestId,
  orderId,
  onSelectHistory,
}: ReEvaluationProps) {
  const [isReEvaluationOpen, setIsReEvaluationOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const [
    getHistories,
    {
      data: histories,
      isLoading: historiesLoading,
      isFetching: historiesFetching,
    },
  ] = useLazyDashboardOrderHistoriesQuery();

  const [reEvaluateAllSteps, { isLoading: isReEvaluating }] =
    useReEvaluateAllStepsMutation();

  useEffect(() => {
    if (isHistoryOpen) {
      getHistories(
        {
          id: orderId,
        },
        false
      );
    }
  }, [getHistories, orderId, isHistoryOpen]);

  useEffect(() => {
    getHistories({
      id: orderId,
    });
  }, [getHistories, orderId]);

  const onReEvaluateAllSteps = () => {
    reEvaluateAllSteps({ order_id: orderId })
      .unwrap()
      .then(() => {
        setIsReEvaluationOpen(false);
        toast.success("Re-evaluation started");
      });
  };

  return (
    <>
      <Flex gap="2" align="center">
        {orderRequestHistory?.is_failed && (
          <Tooltip content={`Note: ${orderRequestHistory.note || "N/A"}`}>
            <Badge size="3" color="red" variant="soft">
              Evaluation failed <QuestionMarkCircledIcon />
            </Badge>
          </Tooltip>
        )}
        <AlertDialog.Root
          open={isReEvaluationOpen}
          onOpenChange={setIsReEvaluationOpen}
        >
          <AlertDialog.Trigger>
            <Button variant="classic">
              <ReloadIcon />
              Re-evaluate all steps
            </Button>
          </AlertDialog.Trigger>
          <AlertDialog.Content>
            <AlertDialog.Title>Re-evaluate all steps</AlertDialog.Title>
            <AlertDialog.Description>
              Are you sure you want to re-evaluate all steps?
            </AlertDialog.Description>
            <Flex gap="2" justify="end" mt="4">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray" className="w-[120px]">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <Button
                variant="solid"
                color="green"
                className="w-[200px]"
                loading={isReEvaluating}
                onClick={onReEvaluateAllSteps}
              >
                Confirm re-evaluation
              </Button>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
        <Popover.Root open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
          <Popover.Trigger>
            <Button variant="surface" color="gray">
              Evaluation History <ChevronDownIcon />
            </Button>
          </Popover.Trigger>
          <Popover.Content minWidth="350px">
            <HistoryList
              requestId={requestId}
              onSelectItem={onSelectHistory}
              data={histories}
              isLoading={historiesLoading}
              isFetching={historiesFetching}
              onRefresh={() => getHistories({ id: orderId }, false)}
              orderRequestHistory={orderRequestHistory}
            />
          </Popover.Content>
        </Popover.Root>
      </Flex>
    </>
  );
}

type HistoryItemProps = {
  history: DashboardOrderDetailsResponse["result"]["order_request_history"];
  isActive: boolean;
  onSelectHistory: (requestId: string | undefined) => void;
};

function HistoryItem({ history, isActive, onSelectHistory }: HistoryItemProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (isActive) {
      ref.current?.scrollIntoView({ block: "start" });
    }
  }, []);

  return (
    <Flex
      ref={ref}
      onClick={() => onSelectHistory(history.request_id)}
      p="2"
      className={twMerge([
        "cursor-pointer border border-[var(--gray-5)] hover:bg-[var(--gray-3)] rounded-md shadow-sm",
        isActive && "bg-[var(--gray-3)] border-[var(--gray-6)]",
        history.is_failed &&
          "bg-[var(--red-3)] hover:bg-[var(--red-4)] border-[var(--red-6)]",
      ])}
    >
      <DataList.Root className="gap-y-1" size="1">
        <DataList.Item>
          <DataList.Label>Started at:</DataList.Label>
          <DataList.Value>{history.started_at || "-"}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>Finished at:</DataList.Label>
          <DataList.Value>{history.finished_at || "-"}</DataList.Value>
        </DataList.Item>
        {history.note && (
          <DataList.Item>
            <DataList.Label>Note:</DataList.Label>
            <DataList.Value>{history.note}</DataList.Value>
          </DataList.Item>
        )}
        <DataList.Item>
          <DataList.Label>Pipeline Version:</DataList.Label>
          <DataList.Value>{history.pipeline_version || "-"}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>Pipeline Description:</DataList.Label>
          <DataList.Value>{history.pipeline_description || "-"}</DataList.Value>
        </DataList.Item>
      </DataList.Root>
    </Flex>
  );
}

type HistoryListProps = {
  data: DashboardOrderHistoriesResponse | undefined;
  isFetching: boolean;
  isLoading: boolean;
  requestId: string | null;
  orderRequestHistory:
    | DashboardOrderDetailsResponse["result"]["order_request_history"]
    | undefined;
  onRefresh: () => void;
  onSelectItem: (requestId: string | undefined) => void;
};

function HistoryList({
  data,
  isFetching,
  isLoading,
  requestId,
  orderRequestHistory,
  onSelectItem,
  onRefresh,
}: HistoryListProps) {
  const isHistoryActive = (history: { request_id: string | null }) => {
    return (
      requestId === history.request_id ||
      orderRequestHistory?.request_id === history.request_id
    );
  };

  return (
    <>
      <Flex justify="between" align="center" mb="2">
        <Heading size="2">Evaluation History</Heading>
        <Button
          variant="soft"
          color="gray"
          size="1"
          loading={isFetching}
          onClick={onRefresh}
        >
          <ReloadIcon />
          Refresh
        </Button>
      </Flex>
      <ScrollArea type="auto" className="max-h-[400px]">
        <Flex direction="column" gap="2">
          {isLoading ? (
            <Flex justify="center" align="center" p="2">
              <Text size="1">Loading...</Text>
            </Flex>
          ) : !data?.results.length ? (
            <Flex justify="center" align="center" p="2">
              <Text color="gray" size="1">
                No evaluation history
              </Text>
            </Flex>
          ) : (
            data?.results.map((history) => (
              <HistoryItem
                key={history.request_id}
                history={history}
                isActive={isHistoryActive(history)}
                onSelectHistory={onSelectItem}
              />
            ))
          )}
        </Flex>
      </ScrollArea>
    </>
  );
}
